import { useEffect, useState } from "react";
import { AppLayout, RiskBanner } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowUpFromLine, AlertTriangle, ShieldCheck } from "lucide-react";

export default function Withdraw() {
  const { user, refresh } = useAuth();
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState(user?.wallet_address || "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pubCfg, setPubCfg] = useState({ withdrawal_deduction_pct: 0, withdrawal_processing_hours: 24 });
  const [eligibility, setEligibility] = useState(null);
  const [summary, setSummary] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = async () => { const { data } = await api.get("/withdrawals/my"); setHistory(data.withdrawals); };
  const loadCfg = async () => { try { const { data } = await api.get("/public/config"); setPubCfg(data); } catch (e) { /* ignore */ } };
  const loadEligibility = async () => { try { const { data } = await api.get("/withdrawals/eligibility"); setEligibility(data); } catch (e) { /* ignore */ } };
  const loadSummary = async () => { try { const { data } = await api.get("/wallet/summary"); setSummary(data); } catch (e) { /* ignore */ } };
  useEffect(() => { load(); loadCfg(); loadEligibility(); loadSummary(); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (user?.wallet_address && !wallet) setWallet(user.wallet_address); }, [user?.wallet_address, wallet]);

  const pending = history.find(w => w.status === "pending");
  const balance = user?.balance || 0;
  const minAct = user?.min_activation_amount || 10;
  // Feb-19: withdrawable = balance - locked_principal (from wallet summary)
  const withdrawable = summary ? Number(summary.withdrawable_profit || 0) : balance;
  const lockedPrincipal = summary ? Number(summary.locked_deposit || 0) : 0;
  const recoveryRemaining = summary ? Number(summary.recovery_remaining || 0) : 0;
  const availableAll = Math.max(0, Math.min(withdrawable, balance));
  const availableKeepMin = Math.max(0, availableAll - minAct);
  const deductionPct = pubCfg.withdrawal_deduction_pct || 0;
  const grossAmt = parseFloat(amount) || 0;
  const deductionAmt = grossAmt * deductionPct / 100;
  const netAmt = Math.max(0, grossAmt - deductionAmt);

  const setAmt = (v) => { setAmount(String(v)); setOtpSent(false); };

  const requestOtp = async () => {
    setBusy(true);
    try {
      await api.post("/withdrawals/request-otp", { amount: parseFloat(amount), wallet_address: wallet });
      setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdrawals/submit", { amount: parseFloat(amount), network: "BEP20", wallet_address: wallet, otp });
      toast.success("Withdrawal request submitted");
      setAmount(""); setOtp(""); setOtpSent(false);
      await refresh(); await load(); await loadEligibility(); await loadSummary();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  // Countdown: for the most-recent pending withdrawal, 24h from created_at
  const pendingCountdown = (() => {
    if (!pending) return null;
    const created = new Date(pending.created_at).getTime();
    const target = created + (pubCfg.withdrawal_processing_hours || 24) * 3600 * 1000;
    const ms = Math.max(0, target - now);
    const h = Math.floor(ms / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return { h, m, s, done: ms === 0, target };
  })();
  // Weekly limit countdown
  const weeklyCountdown = (() => {
    if (!eligibility || eligibility.can_request || !eligibility.next_available_at) return null;
    const target = new Date(eligibility.next_available_at).getTime();
    const ms = Math.max(0, target - now);
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    return { d, h, m, done: ms === 0 };
  })();

  return (
    <AppLayout variant="user">
      <RiskBanner />
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Withdraw USDT (BEP20)</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1 flex items-center gap-3"><ArrowUpFromLine className="text-[#8AE234]"/> Cash out</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          {/* Persistent Deposit Recovery banner — stays until user's income covers deposit */}
          {lockedPrincipal > 0 && (
            <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-amber-200 text-xs" data-testid="recovery-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
                <div>
                  <div className="font-heading font-semibold text-amber-300 text-sm">Deposit Recovery in Progress</div>
                  <div className="mt-1 leading-relaxed">
                    Withdrawable now: <span className="font-mono text-white">${withdrawable.toFixed(2)}</span>. Your deposit principal
                    (<span className="font-mono text-white">${lockedPrincipal.toFixed(2)}</span>) unlocks once your total income reaches your total deposit.
                    Remaining to unlock: <span className="font-mono text-white">${recoveryRemaining.toFixed(2)}</span>.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Available Balance</div>
              <div className="mt-1 font-mono text-2xl neon-text">${(user?.balance||0).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Withdrawable</div>
              <div className="mt-1 font-mono text-2xl text-[#8AE234]">${withdrawable.toFixed(2)}</div>
            </div>
          </div>

          {pending ? (
            <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-amber-200" data-testid="pending-withdrawal-panel">
              <div className="flex items-center gap-2 font-heading font-semibold"><AlertTriangle size={16}/> Pending Withdrawal</div>
              <div className="mt-2 text-sm">${pending.amount.toFixed(2)} → <span className="font-mono">{pending.wallet_address.slice(0,10)}…{pending.wallet_address.slice(-6)}</span>.</div>
              {pending.deduction_amount > 0 && (
                <div className="mt-2 text-xs font-mono grid grid-cols-3 gap-2">
                  <div>Gross: <span className="text-white">${pending.amount.toFixed(2)}</span></div>
                  <div>Fee ({pending.deduction_pct}%): <span className="text-red-300">-${pending.deduction_amount.toFixed(2)}</span></div>
                  <div>Net: <span className="text-[#8AE234]">${pending.net_amount.toFixed(2)}</span></div>
                </div>
              )}
              {pendingCountdown && !pendingCountdown.done ? (
                <div className="mt-3 rounded-lg bg-black/30 border border-amber-500/20 px-3 py-2 text-center" data-testid="withdrawal-countdown">
                  <div className="text-[10px] uppercase tracking-widest text-amber-200/70">Auto-processes within</div>
                  <div className="font-mono text-2xl text-white">{String(pendingCountdown.h).padStart(2,"0")}:{String(pendingCountdown.m).padStart(2,"0")}:{String(pendingCountdown.s).padStart(2,"0")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-200/70 mt-0.5">Target: 24 hours</div>
                </div>
              ) : (
                <div className="mt-2 text-xs">Being processed by the company now — please hold tight.</div>
              )}
            </div>
          ) : weeklyCountdown && !weeklyCountdown.done ? (
            <div className="mt-6 rounded-xl border border-nb-cyan/30 bg-nb-cyan/5 px-4 py-4 text-slate-200" data-testid="weekly-limit-panel">
              <div className="flex items-center gap-2 font-heading font-semibold text-nb-cyan"><ShieldCheck size={16}/> Weekly Withdrawal Limit</div>
              <div className="mt-2 text-sm">You have already made your withdrawal request today. Only 1 withdrawal per calendar day.</div>
              <div className="mt-3 rounded-lg bg-black/30 border border-nb-cyan/20 px-3 py-2 text-center">
                <div className="text-[10px] uppercase tracking-widest text-nb-cyan/70">Next withdrawal available in</div>
                <div className="font-mono text-2xl text-white">{weeklyCountdown.d}d {String(weeklyCountdown.h).padStart(2,"0")}h {String(weeklyCountdown.m).padStart(2,"0")}m</div>
              </div>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-widest">Amount (USDT)</label>
              <input data-testid="withdraw-amount" type="number" step="0.01" min={10} required value={amount} onChange={(e)=>{setAmount(e.target.value); setOtpSent(false);}} className="nb-input mt-1" disabled={otpSent}/>
              <div className="text-xs text-slate-500 mt-1">Minimum $20. BEP20 network only. Processing time: <span className="text-nb-cyan">24 hours</span>. Daily limit: 1 withdrawal per day.</div>
              {/* Feb-19: Inline smart validation — persistent, clear, non-blocking-toast */}
              {grossAmt > 0 && grossAmt > withdrawable && (
                <div className="mt-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="amount-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5"/>
                    <div>
                      You can withdraw up to <span className="font-mono text-white">${withdrawable.toFixed(2)}</span> right now.
                      {lockedPrincipal > 0 && <> The rest (<span className="font-mono">${lockedPrincipal.toFixed(2)}</span>) is your deposit principal and unlocks once total income matches total deposit.</>}
                    </div>
                  </div>
                </div>
              )}
              {grossAmt > 0 && grossAmt >= 10 && grossAmt <= withdrawable && (
                <div className="mt-2 rounded-lg border border-white/5 bg-[#070B14] p-3 text-xs font-mono grid grid-cols-3 gap-2" data-testid="withdraw-breakdown">
                  <div><div className="text-slate-500 uppercase tracking-widest text-[10px]">Gross</div><div className="text-white">${grossAmt.toFixed(2)}</div></div>
                  <div><div className="text-slate-500 uppercase tracking-widest text-[10px]">Fee ({deductionPct}%)</div><div className="text-red-300">-${deductionAmt.toFixed(2)}</div></div>
                  <div><div className="text-slate-500 uppercase tracking-widest text-[10px]">You Receive</div><div className="text-[#8AE234]">${netAmt.toFixed(2)}</div></div>
                </div>
              )}
              {!otpSent && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="withdraw-quick-amounts">
                  <button type="button" data-testid="wd-quick-all" onClick={()=>setAmt(availableAll.toFixed(2))} disabled={availableAll < 10} className="text-[11px] px-3 py-1 rounded-full border border-[#8AE234]/40 text-[#8AE234] hover:bg-[#8AE234]/10 disabled:opacity-40 disabled:cursor-not-allowed">Withdraw all · ${availableAll.toFixed(2)}</button>
                  <button type="button" data-testid="wd-quick-keep-min" onClick={()=>setAmt(availableKeepMin.toFixed(2))} disabled={availableKeepMin < 10} title={`Keeps $${minAct} in your wallet so your account stays Active and ready for the next bot cycle`} className="text-[11px] px-3 py-1 rounded-full border border-nb-cyan/40 text-nb-cyan hover:bg-nb-cyan/10 disabled:opacity-40 disabled:cursor-not-allowed">Keep ${minAct} for next bot · ${availableKeepMin.toFixed(2)}</button>
                  <button type="button" data-testid="wd-quick-half" onClick={()=>setAmt((availableAll/2).toFixed(2))} disabled={availableAll < 20} className="text-[11px] px-3 py-1 rounded-full border border-white/10 text-slate-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed">50% · ${(availableAll/2).toFixed(2)}</button>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-widest">Your BEP20 Wallet Address</label>
              <input data-testid="withdraw-wallet" required value={wallet} onChange={(e)=>{setWallet(e.target.value); setOtpSent(false);}} className="nb-input mt-1 font-mono" placeholder="0x…" disabled={otpSent}/>
            </div>
            {!otpSent ? (
              <button type="button" onClick={requestOtp} data-testid="withdraw-request-otp" disabled={busy || !amount || !wallet || user?.kyc_status !== "approved" || grossAmt > withdrawable || grossAmt < 10} className="nb-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? "Sending OTP…" : (<><ShieldCheck size={14} className="inline mr-1"/> Send Email OTP</>)}
              </button>
            ) : (
              <>
                {otpSent && <div className="rounded-lg border border-[#8AE234]/30 bg-[#8AE234]/5 px-3 py-2 text-xs text-[#8AE234]">OTP sent to your registered email. Please check inbox &amp; spam.</div>}
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-widest">Enter OTP from email</label>
                  <input data-testid="withdraw-otp" required value={otp} onChange={(e)=>setOtp(e.target.value)} maxLength={6} className="nb-input mt-1 font-mono text-2xl tracking-[0.4em] text-center" placeholder="000000"/>
                </div>
                <button data-testid="withdraw-submit" disabled={busy} className="nb-btn-primary w-full">{busy?"Submitting…":"Confirm withdrawal"}</button>
                <button type="button" onClick={()=>{setOtpSent(false); setOtp("");}} className="text-xs text-slate-400 hover:text-white w-full">Change details</button>
              </>
            )}
            {user?.kyc_status !== "approved" && <div className="text-xs text-amber-300">KYC approval required to withdraw.</div>}
          </form>
          )}
        </div>

        <div className="nb-card p-6">
          <div className="font-heading font-semibold text-white mb-4">Withdrawal history</div>
          {history.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No withdrawals yet.</div> : (
            <div className="divide-y divide-white/5">
              {history.map((w) => (
                <div key={w.id} data-testid={`withdraw-history-${w.id}`} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-white">${w.amount.toFixed(2)} <span className="text-xs text-slate-500 ml-2">{w.network}</span></div>
                      <div className="text-xs text-slate-500 font-mono truncate max-w-[220px]">{w.wallet_address}</div>
                    </div>
                    <div className={`text-xs uppercase tracking-widest px-2 py-1 rounded shrink-0 ml-2 ${w.status==="approved"?"text-[#8AE234] bg-[#8AE234]/10":w.status==="pending"?"text-amber-300 bg-amber-500/10":"text-red-400 bg-red-500/10"}`}>{w.status}</div>
                  </div>
                  {w.deduction_amount > 0 && (
                    <div className="mt-2 rounded-lg border border-white/5 bg-[#070B14] px-3 py-2 grid grid-cols-3 gap-2 text-[11px] font-mono">
                      <div><div className="text-slate-500 uppercase tracking-widest text-[9px]">Gross</div><div className="text-white">${w.amount.toFixed(2)}</div></div>
                      <div><div className="text-slate-500 uppercase tracking-widest text-[9px]">Fee ({w.deduction_pct}%)</div><div className="text-red-300">-${w.deduction_amount.toFixed(2)}</div></div>
                      <div><div className="text-slate-500 uppercase tracking-widest text-[9px]">You Received</div><div className="text-[#8AE234]">${w.net_amount.toFixed(2)}</div></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
